import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Button } from 'react-bootstrap';
import _ListingItem from './_ListingItem';
import ChooseItemsModal from 'Components/Modal/ChooseItemsModal';
import EmptyModal from 'Components/Modal/EmptyModal';
import LoadingButton from 'Components/Button/LoadingButton';
import { collectionItemsSelected, setCoverImage } from 'Vendor/Store/studio';
import { selectAllCollectionSelections, selectAssets, selectCoverImage, selectCoverImageId } from 'Vendor/Store/studio/selectors';
import Api from 'Vendor/Api';
import useTranslationDataUtils from 'Vendor/Utils/TranslationDataUtils';
import useHumanizeFileSizeUtils from 'Vendor/Utils/HumanizeFileSizeUtils';
import { useTranslation } from 'react-i18next';
import { mimeTypeImage } from 'Vendor/UploadSystem';
interface CollectionPickerProps {
    collection: any;
    creationId: number;
    type: 'bucket' | 'catalog' | 'external';
    searchTerm?: string;
}

interface ExternalFileBase {
    id: string;
    name: string;
    mimeType?: string;
    path: string;
    bucketPath: string;
    thumbnail: string;
    state: 'uploaded' | 'error';
    file_size: number;
}

interface PendingExternalFile {
    id: string;
    name: string;
    data: File;
    thumbnail: string;
    state: 'pending' | 'uploading' | 'error';
    file_size: number;
    uploadProgress?: number;
    mimeType?: string;
}

const MAX_ATTACHMENT_SIZE = 99_900_000; // 99 MB

const CollectionPicker: React.FC<CollectionPickerProps> = ({ creationId, collection, type, searchTerm }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { translate } = useTranslationDataUtils();
    const { humanFileSize } = useHumanizeFileSizeUtils();

    const selectedItemsByCollection = useSelector(selectAllCollectionSelections);
    const allCanvasAssets = useSelector(selectAssets);
    const coverImage = useSelector(selectCoverImage);
    const coverImageId = useSelector(selectCoverImageId);

    // Modal & external files state
    const [showCollectionModal, setShowCollectionModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // --- External files ---
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, ExternalFileBase>>({});
    const [pendingFiles, setPendingFiles] = useState<Record<string, PendingExternalFile>>({});
    const [fileUploading, setFileUploading] = useState(false);
    const [maxFileSizeError, setMaxFileSizeError] = useState(false);
    const inputFileRef = useRef<HTMLInputElement>(null);

    const handleOpenFileInput = () => inputFileRef.current?.click();
    const resetUploadModal = () => {
        setShowUploadModal(false);
        setMaxFileSizeError(false);
    };
    const getFileName = (filePath: string) => filePath?.split('/').pop() || filePath || '';

    // --- Fetch existing external files on mount ---
    useEffect(() => {
        if (type !== 'external') return;

        Api.get(`/studio/creation/${creationId}/external-files`)
            .then(({ data }) => {
                if (!data?.items) return;
                const fetched: Record<string, ExternalFileBase> = {};
                Object.entries(data.items).forEach(([key, file]: any) => {
                    fetched[key] = { ...file, state: 'uploaded' };
                });
                setUploadedFiles(fetched);
            })
            .catch(err => console.error('Failed to fetch external files', err));
    }, [creationId, type]);

    // --- Handle external file selection ---
    const handleAddFiles = useCallback((files: FileList) => {
        const validFiles = Array.from(files).filter(file =>
            file.type.startsWith("image/") || file.type.startsWith("video/")
        )

        if (validFiles.length !== files.length) {
            console.warn("Some non-image/non-video files were ignored")
        }

        let tempFiles = { ...pendingFiles };
        let totalSize = Object.values(pendingFiles).reduce((sum, f) => sum + f.file_size, 0);
        totalSize += Object.values(uploadedFiles).reduce((sum, f) => sum + f.file_size, 0);

        Array.from(files).forEach(f => totalSize += f.size);
        if (totalSize > MAX_ATTACHMENT_SIZE) {
            setMaxFileSizeError(true);
            return;
        }

        Array.from(files).forEach(file => {
            tempFiles[file.name] = {
                id: file.name,
                name: file.name,
                thumbnail: URL.createObjectURL(file),
                file_size: file.size,
                state: 'pending',
                data: file,
                mimeType: file.type,
            };
        });
        setPendingFiles(tempFiles);
    }, [pendingFiles, uploadedFiles]);

    // --- Upload external files ---
    useEffect(() => {
        const filesToUpload = Object.entries(pendingFiles).filter(([, file]) => file.state === 'pending');
        if (!filesToUpload.length) return;

        filesToUpload.forEach(([key, file]) => {
            const formData = new FormData();
            formData.append('files[]', file.data as File);
            formData.append('subdirectory', 'uploads');

            setPendingFiles(prev => ({ ...prev, [key]: { ...file, state: 'uploading', uploadProgress: 0 } }));
            setFileUploading(true);

            Api.post(`/creation/${creationId}/external-files/upload`, formData, {
                baseURL: `https://s1-upload.${window.location.host.split(':')[0]}${process.env.REACT_APP_BACKEND_PORT ? ':' + process.env.REACT_APP_BACKEND_PORT : ''}`,
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: ({ loaded, total }) => {
                    const percent = total ? Math.min(100, Math.floor((loaded * 100) / total)) : 0;
                    setPendingFiles(prev => ({ ...prev, [key]: { ...file, state: 'uploading', uploadProgress: percent } }));
                }
            })
                .then(({ data }) => {
                    const fetched: Record<string, ExternalFileBase> = {};
                    Object.entries(data).forEach(([k, file]: any) => {
                        fetched[k] = { ...file, state: 'uploaded' };
                    });
                    setUploadedFiles(prev => ({ ...prev, ...fetched }));
                    setFileUploading(false);
                    setPendingFiles(prev => {
                        const temp = { ...prev };
                        delete temp[key];
                        return temp;
                    });
                })
                .catch(() => {
                    setPendingFiles(prev => ({ ...prev, [key]: { ...file, state: 'error' } }));
                    setFileUploading(false);
                });
        });
    }, [pendingFiles, creationId]);

    const handleRemoveFile = (filePath: string, state: string) => {
        if (state === 'uploaded') {
            Api.delete(`/creation/${creationId}/external-files/remove`, {
                baseURL: `https://s1-upload.${window.location.host.split(':')[0]}${process.env.REACT_APP_BACKEND_PORT ? ':' + process.env.REACT_APP_BACKEND_PORT : ''}`,
                data: { filePath }
            }).finally(() => {
                setUploadedFiles(prev => {
                    const temp = { ...prev };
                    delete temp[filePath.split('/').pop()!];
                    return temp;
                });
            });
        } else {
            setPendingFiles(prev => {
                const temp = { ...prev };
                delete temp[filePath.split('/').pop()!];
                return temp;
            });
        }
    };

    // --- Items on canvas ---
    const itemsOnCanvasSet = useMemo(() => {
        const set = new Set<number | string>();
        Object.values(allCanvasAssets || {}).forEach(asset => {
            const isMatch = type === 'external'
                ? asset.collectionSlug === 'external'
                : asset.collectionSlug === collection.primarySlug;
            if (isMatch && asset.itemId != null) {
                set.add(asset.itemId);
                set.add(String(asset.itemId));
                set.add(Number(asset.itemId));
            }
        });
        return set;
    }, [allCanvasAssets, collection.primarySlug, type]);

    const resizedByIdUrl = (id: number, itemType?: string, versionNumber?: number): string => {
        const isProduct = itemType === 'catalog' || itemType === 'product' || itemType === 'PRODUCT';
        const url = isProduct
            ? `${Api.defaults.baseURL}/cdn/product/${id}/resized`
            : `${Api.defaults.baseURL}/cdn/file/${id}/resized`;
        return versionNumber ? `${url}?versionNumber=${versionNumber}` : url;
    };

    // --- Displayed items ---
    const displayedItems = useMemo(() => {
        let items: any[] = [
            ...Object.values(uploadedFiles),
        ];

        if (type === 'external') {
            // Canvas assets
            Object.values(allCanvasAssets || {})
                .filter(a => a.collectionSlug === 'external' && a.itemId)
                .forEach(asset => {
                    if (!items.find(i => i.id === asset.itemId)) {
                        items.push({ id: asset.itemId, name: `Item ${asset.itemId}`, thumbnail: asset.content?.src });
                    }
                });
        } else {
            // bucket/catalog logic
            const itemsFromModal = (creationId && selectedItemsByCollection[creationId]?.[collection.primarySlug]) || [];
            const itemsFromCanvas = Object.values(allCanvasAssets || {})
                .filter((asset: any) => asset.collectionSlug === collection.primarySlug && asset.itemId)
                .map((asset: any) => {
                    const originalItem = collection.items?.find((i: any) => i.id === asset.itemId);
                    return originalItem || {
                        id: asset.itemId,
                        slug: asset.collectionSlug,
                        previewSrc: asset.content?.previewSrc ?? (asset.itemId ? resizedByIdUrl(asset.itemId, asset.type, asset.versionNumber || asset.content?.versionNumber) : undefined),
                        resizedSrc: asset.content?.resizedSrc,
                        view: asset.content?.src,
                        name: `Item ${asset.itemId}`,
                    };
                });

            const combined = new Map<number | string, any>();
            itemsFromModal.forEach(item => combined.set(item.id ?? item.slug, item));
            itemsFromCanvas.forEach(item => combined.set(item.id ?? item.slug, item));

            if (
                type === 'bucket'
                && coverImage
                && coverImage.bucketPrimarySlug === collection.primarySlug
            ) {
                combined.set(coverImage.id ?? coverImage.slug, coverImage);
            }

            items = Array.from(combined.values());
        }

        if (searchTerm?.trim()) {
            const lower = searchTerm.toLowerCase();
            items = items.filter(item => (item.name?.toLowerCase().includes(lower)) || (item.slug?.toLowerCase().includes(lower)));
        }

        return items;
    }, [allCanvasAssets, selectedItemsByCollection, collection, type, searchTerm, uploadedFiles, coverImage]);

    // --- Handle selection ---
    const handleSetSelected = (selected: any[]) => {
        if (!creationId) return;
        const slug = type === 'external' ? 'external' : collection.primarySlug;
        dispatch(collectionItemsSelected({ creationId, collectionSlug: slug, selectedItems: selected }));
    };

    const handleToggleCoverImage = async (fileId: number, isCover: boolean) => {
        const { data } = await Api.patch(`/studio/creation/${creationId}/cover-image`, {
            file: isCover ? null : fileId,
        });

        return data?.coverImage ? Number(data.coverImage) : undefined;
    };

    const externalFiles = useMemo(() => {
        return [
            ...Object.values(pendingFiles).map(file => ({
                name: file.name,
                fileSize: file.file_size,
                state: 'pending',
                filePath: file.name,
            })),
            ...Object.values(uploadedFiles).map(file => ({
                name: file.name,
                fileSize: file.file_size,
                state: file.state ?? 'uploaded',
                filePath: file.bucketPath,
            })),
        ];
    }, [pendingFiles, uploadedFiles]);

    const modalPath = type === 'external' ? '/external' : `/${type.charAt(0)}/${collection.primarySlug}`;

    const isVideoItem = (item: any) => {
        const mime = item.viewMimeType || item.mimeType;
        if (mime?.startsWith('video/')) return true;
        const src = item.view || item.name || '';
        return /\.(mp4|webm|ogg|mov|m4v|avi|flv)(.*)?$/i.test(src);
    };

    const getImageUrl = (item: any) => {
        if (type === 'external') return item.thumbnail;

        // Videos have a dedicated image thumbnail generated by the backend — always use it in the sidebar
        if (isVideoItem(item)) {
            return item.thumbnail?.replace('__SIZING__', 'medium')
                || item.previewSrc
                || item.view
                || '';
        }

        // For images already on the canvas, reuse the canvas resized image URL
        // so the browser cache is shared instead of firing a separate thumbnail request
        if (itemsOnCanvasSet.has(item.id)) {
            const canvasSrc = getCanvasSrc(item);
            if (canvasSrc) return canvasSrc;
        }

        return item.previewSrc
            || item.thumbnail?.replace('__SIZING__', 'medium')
            || item.view
            || '';
    };

    const getCanvasSrc = (item: any) => {
        if (type === 'external') return item.thumbnail;

        const isVideo = isVideoItem(item);

        return item.resizedSrc
            || (item.id && !isVideo ? resizedByIdUrl(item.id, type, item.versionNumber || item.lastVersionNumber) : null)
            || item.view
            || '';
    };
    const canBeCoverImage = (item: any) =>
        type === 'bucket' && (item.mimeType ? mimeTypeImage.includes(item.mimeType) : Boolean(getImageUrl(item)));

    return (
        <Accordion.Item eventKey={collection.primarySlug} className="mb-2 studio-accordion">
            <Accordion.Header className='studio-accordion-header d-flex justify-content-between position-relative'>
                {type === 'external' ? t('app.creation.external_files.label') : translate(collection, 'name')}
                <Button
                    className="listing-item-wrapper add-item-button d-flex align-items-center justify-content-center border-1 border rounded-sm"
                    style={{ height: '3vh', width: '40px', position: 'absolute', right: '0px' }}
                    onClick={(e) => { e.stopPropagation(); type === 'external' ? setShowUploadModal(true) : setShowCollectionModal(true); }}
                >
                    <i className="fa-solid fa-plus fs-5 text-white"></i>
                </Button>
            </Accordion.Header>

            <Accordion.Body className='studio-accordion-body'>
                <div className="listing-grid mb-3">
                    {displayedItems.map(item => (
                        <_ListingItem
                            key={item.id}
                            id={item.id}
                            imageUrl={getImageUrl(item)}
                            canvasSrc={getCanvasSrc(item)}
                            originalSrc={item.view}
                            imageAlt={item.name || item.slug}
                            isDraggable
                            itemId={item.id}
                            contentType={type === 'bucket' ? 'file' : type === 'catalog' ? 'product' : 'external'}
                            collectionSlug={type === 'external' ? 'external' : item.bucketPrimarySlug || item.catalogPrimarySlug}
                            type="existing-image"
                            className="listing-item-wrapper"
                            isInCanvas={itemsOnCanvasSet.has(item.id)}
                            showCoverToggle={canBeCoverImage(item)}
                            isCover={canBeCoverImage(item) && coverImageId === item.id}
                            onCoverToggle={canBeCoverImage(item)
                                ? async () => {
                                    try {
                                        const nextCoverImageId = await handleToggleCoverImage(item.id, coverImageId === item.id);
                                        dispatch(setCoverImage(nextCoverImageId ? item : null));
                                    } catch (error) {
                                        console.error('Failed to update cover image', error);
                                    }
                                }
                                : undefined}
                            mimeType={item.viewMimeType || item.mimeType}
                        />
                    ))}
                </div>

                {/* Collection selection modal */}
                {type !== 'external' && (
                    <ChooseItemsModal
                        show={showCollectionModal}
                        setShow={setShowCollectionModal}
                        title={translate(collection, 'name')}
                        path={modalPath}
                        entity={type}
                        primarySlug={collection.primarySlug}
                        multiple
                        selected={displayedItems}
                        setSelected={handleSetSelected}
                        selectionNamespace="studio"
                    />
                )}

                {/* External upload modal */}
                {type === 'external' && (
                    <EmptyModal show={showUploadModal} onClose={resetUploadModal} title={t('app.creation.external_files.label')} fullscreen>
                        <ul className="grouped-action-choices">
                            <li className="btn btn-secondary" onClick={handleOpenFileInput}>
                                <label htmlFor="external-files-input" onClick={e => e.stopPropagation()}>
                                    {t('app.creation.external_files.add')}
                                </label>
                                <input
                                    ref={inputFileRef}
                                    type="file"
                                    id="external-files-input"
                                    className="d-none"
                                    multiple
                                    accept="image/*,video/*"
                                    onChange={e => e.target.files && handleAddFiles(e.target.files)}
                                />
                            </li>
                            {externalFiles.map((file, idx) => (
                                <li key={idx} className="d-flex justify-content-between align-items-center">
                                    <div><span className="name">{getFileName(file.name)}</span></div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="size">{humanFileSize(file.fileSize)}</span>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveFile(file.filePath, file.state)}>
                                            <i className="fa-solid fa-x"></i>
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {maxFileSizeError && <div className="alert alert-danger">File size limit exceeded</div>}

                        {externalFiles.length > 0 && (
                            <div className="d-flex justify-content-end mt-3 mb-2">
                                <LoadingButton isLoading={fileUploading} variant="primary" onClick={resetUploadModal}>Sauvegarder et fermer</LoadingButton>
                            </div>
                        )}
                    </EmptyModal>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default CollectionPicker;
